import { makeAutoObservable, runInAction } from 'mobx'

import { getErrorMessage } from '../Helpers/getErrorMessage'
import { CompleteTaskTask } from '../Tasks/CompleteTaskTask'

import type { Notify } from '../Contracts/Notify'

/** Ціль модалки завершення — стору потрібен лише `uuid` завантаженої задачі. */
export interface CompleteTaskTarget {
    uuid?: string
}

/**
 * Локальний стор `CompleteTaskModal` (SPEC-008 §8.2).
 *
 * Збирає витрачений час (години + хвилини → `timeSpentInMinutes`) і завершує задачу
 * через `CompleteTaskTask`. Еталон — autostrada `ConfirmExecutionModalStore.ts`
 * (`makeAutoObservable`, `openFor/close/confirm(notify, reload)`), замість легасі
 * callback-стилю `CompleteTaskModalStore.js`. Інстанс — per-mount у `TaskViewActions`.
 */
export class CompleteTaskModalStore {
    open = false
    task: CompleteTaskTarget | null = null
    hours = 0
    minutes = 0
    error = false
    loading = false

    constructor() {
        makeAutoObservable(this)
    }

    openFor(task: CompleteTaskTarget): void {
        this.task = task
        this.hours = 0
        this.minutes = 0
        this.error = false
        this.loading = false
        this.open = true
    }

    close(): void {
        this.open = false
        this.task = null
        this.hours = 0
        this.minutes = 0
        this.error = false
        this.loading = false
    }

    setHours(v: number): void {
        this.hours = v
        this.error = false
    }

    setMinutes(v: number): void {
        this.minutes = v
        this.error = false
    }

    get totalMinutes(): number {
        return (this.hours || 0) * 60 + (this.minutes || 0)
    }

    /**
     * Завершує задачу. Час обов'язковий (> 0) — перенос легасі-валідатора
     * `CompleteTaskModal.jsx:15-18`. Успіх → notify.success + reload + close.
     * Помилка → notify.error, модалка лишається відкритою, `loading=false`.
     */
    async confirm(notify: Notify, reload?: () => void): Promise<boolean> {
        if (!this.task?.uuid) return false
        if (this.totalMinutes <= 0) {
            this.error = true
            return false
        }
        this.loading = true
        try {
            await CompleteTaskTask(this.task.uuid, { timeSpentInMinutes: this.totalMinutes })
            notify.success('Завдання завершено')
            reload?.()
            this.close()
            return true
        } catch (e) {
            notify.error(getErrorMessage(e) || 'Не вдалося завершити завдання')
            runInAction(() => {
                this.loading = false
            })
            return false
        }
    }
}
